import React from "react";
import { flatten, isEmpty, throttle } from "lodash-es";
import { useSearchParams, useLocation } from "react-router";
import { useAppSelector } from "../hooks";

import { permalinkStateSelector } from "../selectors";
import { useViewStore } from "../slices/view";
import { stringifyQuery } from "../querystring";
import { useCesiumContext } from "./cesium/CesiumContext";
import { useTrackedEntity } from "./cesium/hooks";
import { isDefined } from "src/lib/utils";
import { useGeoJSON } from "./hooks/useGeoJSON";
import * as GeoJSON from "src/lib/geo/formats/geojson";
import { X } from "lucide-react";

const getQueryString = throttle((obj, cb) => {
  cb(stringifyQuery(obj), { replace: true });
}, 500);

function toMaxDigits(num: number, digits: number) {
  return parseFloat(num.toFixed(digits));
}
const empty_obj = {};

export function useSerializedUserGeoJSON() {
  const { data } = useGeoJSON("@@user-defined");
  return React.useMemo(() => {
    const features = data?.features;
    if (Array.isArray(features) && features.length > 0) {
      if (features.length > 100) {
        console.warn("serializing features: caution too many features");
        return;
      }
      if (
        GeoJSON.coordAll({ type: "FeatureCollection", features }).length > 100
      ) {
        console.warn("serializing feature: caution too complex geometries");
        //return;
      }
      return features
        .map((feature) => {
          if (feature.geometry.type === "GeometryCollection") return;
          let coords = GeoJSON.coordAll(feature);
          if (feature.geometry.type === "LineString") {
            // if first and last coordinate are the same
            const first = coords[0];
            const last = coords[coords.length - 1];
            if (first[0] === last[0] && first[1] === last[1]) {
              last[0] += 0.0000001;
            }
          }
          let outCoords = flatten(coords)
            .map((x) => isDefined(x) && toMaxDigits(x, 7))
            .join(",");
          if (!isEmpty(feature?.properties)) {
            outCoords += "@@" + JSON.stringify(feature.properties);
          }
          return outCoords;
        })
        .join("|");
    }
  }, [data]);
}
export default function QueryString() {
  const state = useAppSelector(permalinkStateSelector);
  const { view } = useViewStore();
  const [, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { map } = useCesiumContext();
  const trackedEntity = useTrackedEntity(map);
  const viewState = React.useMemo(() => {
    const { camera, extent, prjExtent } = view;
    if (camera) {
      return { camera };
    }
    if (prjExtent) {
      return {
        prjExtent: prjExtent?.map((x) => parseFloat(x.toFixed(7))),
      };
    }
    if (extent) {
      return {
        extent: extent?.map((x) => parseFloat(x.toFixed(7))),
      };
    }

    return {};
  }, [view]);
  const tracking = React.useMemo(() => {
    if (isDefined(trackedEntity) && isDefined(trackedEntity.id)) {
      const t = [trackedEntity.id];
      // @ts-ignore
      const dsName = trackedEntity?.entityCollection?.owner?.name;
      if (dsName) {
        t.unshift(dsName);
      }
      return t;
    }
    return undefined;
  }, [trackedEntity]);
  const features = useSerializedUserGeoJSON();
  React.useEffect(() => {
    getQueryString(
      { ...viewState, ...state, tracking, features },
      setSearchParams
    );
  }, [state, viewState, location.pathname, tracking, setSearchParams]);

  return null;
}
